import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Navigate, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import {
  Gamepad2, Loader2, LogIn, PanelRightClose, PanelRightOpen, Sparkles, Trophy,
} from 'lucide-react';
import client from '../lib/api';
import GameModal from '../components/GameModal';
import MediaThumb from '../components/MediaThumb';
import { useAuth } from '../context/AuthContext';
import { useActiveTheme } from '../lib/activeTheme';
import { themeUsesGamePage } from '../lib/siteThemes';
import {
  GAME_WINS_LIMIT, MODE_DEMO, MODE_REAL, VOLATILITY_MAX_LEVEL, formatWinTime,
  gameDemoAvailable, gameModeFromSearch, gamePagePath, gameVolatility,
  formatWinThreshold, gameWinsPath, pickRecommendations,
} from '../lib/gamePage';
import { HOOKS, gamePageHook, hook } from '../lib/domHooks';
import './GamePage.css';

// Sağ sütundaki öneri sayısı. Liste sunucunun "sık oynanan" ucundan gelir
// (üyede kendi geçmişi, ziyaretçide sitenin geneli) ve açık oyun çıkarıldığı
// için bir fazlası istenir.
export const RECOMMENDATION_LIMIT = 12;

// Sinema modu (öneri sütunu gizli, oyun geniş) oyuncunun tercihidir ve
// tarayıcıda durur — Herkül'ün sohbet rayı ve Vega'nın görünüm anahtarıyla
// aynı yaklaşım.
const THEATER_KEY = 'game-page:theater';

const readTheater = () => {
  try { return window.localStorage.getItem(THEATER_KEY) === '1'; } catch { return false; }
};

const writeTheater = (value) => {
  try { window.localStorage.setItem(THEATER_KEY, value ? '1' : '0'); } catch { /* yok sayılır */ }
};

// Oyun sayfası açıkken `<body>`e yazılan sınıf. Betrun masaüstünde içeriği %80
// ölçekte çizer (betrun.css); oyun küçülmüş açılmasın diye bu sayfada ölçek
// kaldırılır. Sınıf sayfadan çıkarken bırakılır.
export const GAME_PAGE_BODY_CLASS = 'game-page-open';

const categoryLabel = (game) => {
  if (game?.category === 'live-casino' || game?.live) return 'Canlı Casino';
  if (game?.category === 'crash') return 'Hızlı Oyun';
  return 'Slot';
};

/**
 * Adresle açılan oyunun kaydı: geçiş durumunda geldiyse o, yoksa sunucudan.
 *
 * Geçiş durumundaki kayıt her zaman tam değildir — freespin ve çapa ödülünün
 * "Oyna" düğmesi yalnızca kimlik ve ad taşıyabilir. Görseli olmayan kayıt bu
 * yüzden arka planda sunucudan tamamlanır; oynatıcı beklemez (GameModal
 * oturumu yalnızca oyun kimliğine bağlar, kaydın tazelenmesi launch'ı
 * tekrarlamaz). Tamamlama başarısızsa eldeki kayıt kalır.
 */
const useGameRecord = (gameId, stateGame) => {
  const initial = stateGame && String(stateGame.id) === String(gameId) ? stateGame : null;
  const [record, setRecord] = useState({ game: initial, status: initial ? 'ready' : 'loading' });

  useEffect(() => {
    if (stateGame && String(stateGame.id) === String(gameId)) {
      setRecord({ game: stateGame, status: 'ready' });
      if (stateGame.image) return undefined;
      let active = true;
      client.get(`/games/item/${encodeURIComponent(gameId)}`)
        .then(({ data }) => {
          if (active && data) setRecord({ game: { ...stateGame, ...data }, status: 'ready' });
        })
        .catch(() => { /* eldeki kayıt yeterli */ });
      return () => { active = false; };
    }
    let active = true;
    setRecord({ game: null, status: 'loading' });
    client.get(`/games/item/${encodeURIComponent(gameId)}`)
      .then(({ data }) => { if (active) setRecord({ game: data, status: 'ready' }); })
      .catch((error) => {
        if (!active) return;
        setRecord({
          game: null,
          status: error?.response?.status === 404 ? 'missing' : 'error',
        });
      });
    return () => { active = false; };
  }, [gameId, stateGame]);

  return record;
};

/** Sağ sütunun önerileri. Oyun değişince yeniden istenir. */
const useRecommendations = (game, signedIn) => {
  const [items, setItems] = useState(null);
  const gameId = game?.id;
  const category = game?.category;

  useEffect(() => {
    if (!gameId) return undefined;
    let active = true;
    const params = new URLSearchParams({
      scope: signedIn ? 'personal' : 'global',
      limit: String(RECOMMENDATION_LIMIT + 1),
    });
    if (category) params.set('category', category);
    client.get(`/games/popular?${params.toString()}`)
      .then(({ data }) => {
        if (active) setItems(pickRecommendations(data?.items, gameId, RECOMMENDATION_LIMIT));
      })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, [gameId, category, signedIn]);

  return items;
};

/**
 * Oyunun son kazançları ve tablonun eşiği (panel ayarı, varsayılan 10x).
 * Oyun değişince yeniden istenir; hata tabloyu boş gösterir.
 */
const useGameWins = (gameId) => {
  const [state, setState] = useState({ items: null, threshold: null });

  useEffect(() => {
    if (!gameId) return undefined;
    let active = true;
    setState({ items: null, threshold: null });
    client.get(gameWinsPath(gameId), { params: { limit: GAME_WINS_LIMIT } })
      .then(({ data }) => {
        if (!active) return;
        setState({
          items: Array.isArray(data?.items) ? data.items : [],
          threshold: data?.min_multiplier ?? null,
        });
      })
      .catch(() => { if (active) setState({ items: [], threshold: null }); });
    return () => { active = false; };
  }, [gameId]);

  return state;
};

const winMoney = (value) => Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * "Son Kazançlar" tablosu: bu oyunda bahsinin en az eşik katını (panel
 * ayarı, varsayılan 10x) kazandıran son turlar. Eşik ve maskeleme
 * sunucudadır; bahsi olmayan serbest tur listeye hiç gelmez ("Freespin" dalı
 * yalnızca çarpansız eski yanıt için durur). Eşik cümlenin içinde değil,
 * başlığın yanındaki rozette yazılır; boş tablo ikonlu bir kutudur.
 */
const RecentWins = ({ items, threshold }) => {
  if (items === null) return null;
  return (
    <section className="game-page-wins" {...gamePageHook('wins')}>
      <div className="game-page-wins-head">
        <Trophy size={16} />
        <h2>Son Kazançlar</h2>
        <span
          className="game-page-wins-badge"
          title={`Bahsin en az ${formatWinThreshold(threshold)} katı kazanç`}
          {...gamePageHook('wins-badge')}
        >
          {formatWinThreshold(threshold)}+
        </span>
      </div>
      {items.length === 0 ? (
        <div className="game-page-wins-empty" {...gamePageHook('wins-empty')}>
          <span className="game-page-wins-empty-icon" aria-hidden="true">
            <Sparkles size={22} />
          </span>
          <strong>Büyük kazanç bekleniyor</strong>
          <span>İlk büyük kazanan sen ol!</span>
        </div>
      ) : (
        <div className="game-page-wins-wrap">
          <table className="game-page-wins-table">
            <thead>
              <tr>
                <th>Oyuncu</th>
                <th>Zaman</th>
                <th className="is-num">Bahis Tutarı</th>
                <th className="is-num">Çarpan</th>
                <th className="is-num">Ödeme</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, index) => (
                <tr key={`${row.created_at}-${row.user}-${index}`} {...gamePageHook('win-row')}>
                  <td className="game-page-wins-user">{row.user}</td>
                  <td className="game-page-wins-time">{formatWinTime(row.created_at)}</td>
                  <td className="is-num">{winMoney(row.bet)} ₺</td>
                  <td className="is-num">
                    {row.free_round || row.multiplier == null ? (
                      <span className="game-page-wins-free">Freespin</span>
                    ) : (
                      `${Number(row.multiplier).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`
                    )}
                  </td>
                  <td className="is-num game-page-wins-payout">+{winMoney(row.win)} ₺</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const ModeSwitch = ({ mode, onChange }) => (
  <div className="game-page-mode" role="group" aria-label="Oyun modu" {...gamePageHook('mode-switch')}>
    <button
      type="button"
      className={mode === MODE_DEMO ? 'is-active' : ''}
      aria-pressed={mode === MODE_DEMO}
      onClick={() => onChange(MODE_DEMO)}
    >
      Eğlence Oyunu
    </button>
    <button
      type="button"
      className={mode === MODE_REAL ? 'is-active' : ''}
      aria-pressed={mode === MODE_REAL}
      onClick={() => onChange(MODE_REAL)}
    >
      Gerçek Oyun
    </button>
  </div>
);

/** Oyunun volatilitesi: etiket + dolu çubuklar. Bilinmiyorsa hiç çizilmez. */
const VolatilityBadge = ({ game }) => {
  const info = gameVolatility(game);
  if (!info) return null;
  return (
    <span
      className="game-page-info-volatility"
      data-level={info.key}
      title={`Volatilite: ${info.label}`}
      {...gamePageHook('volatility')}
    >
      <span className="game-page-volatility-bars" aria-hidden="true">
        {Array.from({ length: VOLATILITY_MAX_LEVEL }, (_, index) => (
          <i key={index} className={index < info.level ? 'is-on' : ''} />
        ))}
      </span>
      <span>Volatilite: {info.label}</span>
    </span>
  );
};

const Recommendations = ({ items, title, onPick }) => (
  <aside className="game-page-sidebar" {...gamePageHook('sidebar')}>
    <div className="game-page-sidebar-title" {...gamePageHook('sidebar-title')}>
      <Sparkles size={16} />
      <span>{title}</span>
    </div>
    {items === null ? (
      <div className="game-page-sidebar-empty">
        <Loader2 size={18} className="animate-spin" />
      </div>
    ) : items.length === 0 ? (
      <div className="game-page-sidebar-empty" {...gamePageHook('empty')}>
        Şu anda önerilecek oyun yok.
      </div>
    ) : (
      <ul className="game-page-recs" {...gamePageHook('recommendations')}>
        {items.map((game) => (
          <li key={game.id}>
            <button
              type="button"
              className="game-page-rec"
              onClick={() => onPick(game)}
              aria-label={`${game.name} oyununu aç`}
              {...gamePageHook('recommendation')}
            >
              <span className="game-page-rec-art">
                <MediaThumb src={game.image} alt={game.name} />
                {!game.image && <Gamepad2 size={22} />}
              </span>
              <span className="game-page-rec-text">
                <span className="game-page-rec-name">{game.name}</span>
                {game.provider && <span className="game-page-rec-provider">{game.provider}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </aside>
);

/**
 * Oyunun kendi sayfası (`/oyun/:gameId`, bugün yalnızca Betrun).
 *
 * Oyun pencerede değil sayfanın içinde açılır: solda oyun ve altında araç
 * şeridi (mod anahtarı, sinema modu, tam ekran), altında oyunun bilgi kartı
 * (kapak görseliyle) ve "Son Kazançlar" tablosu, sağda öneri sütunu.
 * Launch ve oturum mantığı pencereyle ortaktır
 * (`GameModal` → `layout="page"`).
 */
const GamePage = () => {
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const enabled = themeUsesGamePage(useActiveTheme());
  const { game, status } = useGameRecord(gameId, location.state?.game || null);
  const requestedMode = gameModeFromSearch(location.search);
  const demoAvailable = gameDemoAvailable(game);
  // Demosu olmayan oyunda `?mod=demo` gerçek oyuna düşer (canlı masa, eski bağlantı).
  const mode = requestedMode === MODE_DEMO && game && !demoAvailable ? MODE_REAL : requestedMode;
  const recommendations = useRecommendations(game, !!user);
  const wins = useGameWins(status === 'ready' ? game?.id : null);
  const [theater, setTheater] = useState(readTheater);

  useEffect(() => {
    document.body.classList.add(GAME_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(GAME_PAGE_BODY_CLASS);
  }, []);

  // Öneriden başka bir oyuna geçilince sayfa yeni oyunun başına döner.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* jsdom */ }
  }, [gameId]);

  const changeMode = useCallback((next) => {
    if (!game) return;
    navigate(gamePagePath(game.id, next), { replace: true, state: { game } });
  }, [game, navigate]);

  const mainRef = useRef(null);

  const toggleTheater = useCallback(() => {
    setTheater((value) => { writeTheater(!value); return !value; });
  }, []);

  // Sinema moduna geçince sayfa oynatıcıya kayar: büyüyen sahne kategori
  // barının payını kullanır ve ekranın altına taşmamalıdır (GamePage.css).
  const theaterMounted = useRef(false);
  useEffect(() => {
    if (!theaterMounted.current) { theaterMounted.current = true; return; }
    if (!theater) return;
    try { mainRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch { /* jsdom */ }
  }, [theater]);

  // Oyundan çıkış geldiği sayfaya döner; adres doğrudan açıldıysa (geçmiş
  // yok) ana sayfaya.
  const exit = useCallback(() => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate('/');
  }, [location.key, navigate]);

  const pick = useCallback((next) => {
    // Oturumsuz ziyaretçi demosu olan oyunu demoda izler; olmayanda sayfa
    // giriş kartını çizer.
    const nextMode = !user && gameDemoAvailable(next) ? MODE_DEMO : MODE_REAL;
    navigate(gamePagePath(next.id, nextMode), { state: { game: next } });
  }, [navigate, user]);

  const openLogin = useCallback(() => {
    // Kabuk (BetrunLayout) adresteki `auth: 'login'` durumunu görünce giriş
    // penceresini açar ve durumu temizler; oyuncu aynı sayfada kalır.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: { game, auth: 'login' } });
  }, [game, location.pathname, location.search, navigate]);

  if (!enabled) return <Navigate to="/" replace />;

  const needsLogin = ready && !user && mode === MODE_REAL;
  const sidebarTitle = user ? 'Sana Özel Oyunlar' : 'Popüler Oyunlar';

  const toolbarExtra = (
    <>
      {demoAvailable && <ModeSwitch mode={mode} onChange={changeMode} />}
      <button
        type="button"
        onClick={toggleTheater}
        className="side-icon-btn game-modal-btn game-page-theater-btn inline-flex"
        title={theater ? 'Önerileri göster' : 'Sinema modu'}
        aria-label={theater ? 'Önerileri göster' : 'Sinema modu'}
        aria-pressed={theater}
      >
        {theater ? <PanelRightOpen size={15} /> : <PanelRightClose size={15} />}
      </button>
    </>
  );

  let player;
  if (status === 'loading' || !ready) {
    player = (
      <div className="game-page-placeholder">
        <Loader2 size={28} className="animate-spin text-[var(--pusu-gold)]" />
      </div>
    );
  } else if (status !== 'ready' || !game) {
    player = (
      <div className="game-page-placeholder" {...gamePageHook('empty')}>
        <Gamepad2 size={32} />
        <div className="game-page-placeholder-title">
          {status === 'missing' ? 'Oyun bulunamadı' : 'Oyun yüklenemedi'}
        </div>
        <div className="game-page-placeholder-text">
          {status === 'missing'
            ? 'Bu oyun artık yayında değil. Önerilerden ya da oyun listesinden başka bir oyun seçebilirsiniz.'
            : 'Bağlantınızı kontrol edip sayfayı yenileyin.'}
        </div>
        <button type="button" className="gold-btn px-5 py-2.5 rounded-lg text-sm" onClick={() => navigate('/slotlar')}>
          Oyunlara Dön
        </button>
      </div>
    );
  } else if (needsLogin) {
    player = (
      <div className="game-page-placeholder" {...gamePageHook('login')}>
        <LogIn size={30} />
        <div className="game-page-placeholder-title">{game.name}</div>
        <div className="game-page-placeholder-text">
          Gerçek parayla oynamak için giriş yapın.
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <button type="button" className="gold-btn px-5 py-2.5 rounded-lg text-sm" onClick={openLogin}>
            Giriş Yap
          </button>
          {demoAvailable && (
            <button type="button" className="gold-outline-btn px-5 py-2.5 rounded-lg text-sm" onClick={() => changeMode(MODE_DEMO)}>
              Eğlence Oyunu
            </button>
          )}
        </div>
      </div>
    );
  } else {
    player = (
      <GameModal
        key={`${game.id}-${mode}`}
        game={game}
        mode={mode}
        layout="page"
        onClose={exit}
        toolbarExtra={toolbarExtra}
      />
    );
  }

  return (
    <div className={`game-page${theater ? ' is-theater' : ''}`} {...hook(HOOKS.GAME_PAGE)}>
      <div className="game-page-main" ref={mainRef} {...gamePageHook('main')}>
        {player}
        {game && (
          <div className="game-page-info" {...gamePageHook('info')}>
            {/* Kapak oyunun kendi görselidir (panelden konan görsel ya da
                hareketli kart dahil); görseli olmayan oyunda ikon kalır. */}
            <span className="game-page-info-art" {...gamePageHook('cover')}>
              <MediaThumb src={game.image} alt={game.name} />
              {!game.image && <Gamepad2 size={22} />}
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="game-page-info-name">{game.name}</h1>
              <div className="game-page-info-meta">
                {game.provider && <span>{game.provider}</span>}
                <span>{categoryLabel(game)}</span>
                <VolatilityBadge game={game} />
                {mode === MODE_DEMO && <span className="game-page-info-demo">Eğlence Oyunu</span>}
              </div>
            </div>
          </div>
        )}
        {game && status === 'ready' && <RecentWins items={wins.items} threshold={wins.threshold} />}
      </div>
      {/* Sinema modunda sütun yalnızca masaüstünde gizlenir (CSS); dar
          ekranda öneriler zaten oyunun altındadır. */}
      <Recommendations items={recommendations} title={sidebarTitle} onPick={pick} />
    </div>
  );
};

export default GamePage;
